import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useGeneral } from '../../contexts';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { useChannels } from '../../hooks/useChannels';
import { useEvents } from '../../hooks/useEvents';
import { useEventDetails } from '../../hooks/useEventDetails';
import { createSlug, extractEventPathFromAdminUrl, extractEventIdFromAdminUrl, generateEventAdminUrl } from '../../utils/urlSlug';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ChannelInitialIcon from '../../components/ui/ChannelInitialIcon/ChannelInitialIcon';
import Button from '../../components/ui/Button/Button';
import { SelectorDropdown, PageSelector, PageSelectorItem } from '../../components/organizerPanel';
import { UnauthorizedPage } from '../UnauthorizedPage';
import SEO from '../../components/SEO';
import { ChannelListItem } from '../../types/channel.types';
import { EventListItem } from '../../types/event.types';
import styles from './OrganizerPanelPage.module.css';

const OrganizerPanelPage: React.FC = () => {
  const { orgName, eventPath } = useParams<{ orgName: string; eventPath?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, language } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const userId = user?.id || '';
  const { onNavigateAway, hasUnsavedChanges } = useUnsavedChanges();

  // Detect if we're in event mode
  const isEventMode = useMemo(() => {
    return !!eventPath || !!extractEventPathFromAdminUrl(location.pathname);
  }, [eventPath, location.pathname]);

  // Fetch user's channels to check admin status
  const { channels: userChannels, loading: channelsLoading, refresh: refreshChannels } = useChannels({
    token: token || null,
    userID: userId,
    popular: false
  });

  // Find the channel matching the orgName slug
  const currentChannel = useMemo(() => {
    if (!orgName || !userChannels.length) return null;
    
    return userChannels.find((channel) => {
      const channelTitle = channel.channelTitle || channel.title;
      const channelSlug = channelTitle ? createSlug(channelTitle) : '';
      return channelSlug === orgName;
    });
  }, [orgName, userChannels]);

  // Check if user is admin of this channel
  const isAdmin = useMemo(() => {
    if (!currentChannel) return false;
    return String(currentChannel.isAdmin) === "1";
  }, [currentChannel]);

  // Filter admin channels
  const adminChannels = useMemo(() => {
    return userChannels.filter(channel => String(channel.isAdmin) === "1");
  }, [userChannels]);

  // Fetch events for event mode
  const { events: channelEvents, loading: eventsLoading } = useEvents({
    token: token || null,
    channelID: currentChannel?.channelID || '',
  });

  // Extract eventID from URL for event mode
  const eventIDFromUrl = useMemo(() => {
    if (!isEventMode) return null;
    return extractEventIdFromAdminUrl(location.pathname);
  }, [isEventMode, location.pathname]);

  // Find current event in event mode
  const currentEvent = useMemo(() => {
    if (!isEventMode || !eventPath || !eventIDFromUrl) return null;
    return channelEvents.find(event => String(event.eventID) === eventIDFromUrl);
  }, [isEventMode, eventPath, eventIDFromUrl, channelEvents]);

  // Fetch event details when currentEvent is null but we have eventID from URL
  // This handles the case when navigating to a newly created event before the events list is refreshed
  const { event: fetchedEventDetails } = useEventDetails({
    token: token || null,
    eventID: (!currentEvent && eventIDFromUrl) ? eventIDFromUrl : '',
    loadingKey: undefined // Don't use centralized loading to avoid conflicts
  });

  // Use fetched event details if currentEvent is not available
  const effectiveCurrentEvent = currentEvent || (fetchedEventDetails ? {
    eventID: fetchedEventDetails.eventID,
    eventTitle: fetchedEventDetails.eventTitle || fetchedEventDetails.title,
    title: fetchedEventDetails.title || fetchedEventDetails.eventTitle,
    cover: fetchedEventDetails.cover || fetchedEventDetails.bannerImage,
    image: fetchedEventDetails.image,
    bannerImage: fetchedEventDetails.bannerImage,
    eventStartDateTime: fetchedEventDetails.eventStartDateTime,
  } as EventListItem : null);

  // Sort events by date (most recent first)
  const sortedEvents = useMemo(() => {
    return [...channelEvents].sort((a, b) => {
      const aTime = a.eventStartDateTime || 0;
      const bTime = b.eventStartDateTime || 0;
      return bTime - aTime; // Descending order
    });
  }, [channelEvents]);

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Check if we should show unauthorized page
  const shouldShowUnauthorized = useMemo(() => {
    if (channelsLoading) return false;
    // If channel exists but user is not admin, show unauthorized
    if (currentChannel && !isAdmin) return true;
    // If channel doesn't exist, don't show unauthorized (will show not found elsewhere)
    return false;
  }, [currentChannel, isAdmin, channelsLoading]);

  // Navigation items - channel mode
  const channelNavItems: PageSelectorItem[] = [
    { key: 'events', translationKey: 'organizerPanel.events', path: '' },
    { key: 'settings', translationKey: 'organizerPanel.communitySettings', path: '/settings' },
    { key: 'members', translationKey: 'organizerPanel.communityMembers', path: '/members' },
    { key: 'segments', translationKey: 'organizerPanel.segments', path: '/segments' },
    { key: 'mailing', translationKey: 'organizerPanel.announcements', path: '/mailing' },
    { key: 'revenue', translationKey: 'organizerPanel.communityRevenue', path: '/revenue' },
  ];

  // Navigation items - event mode
  const eventNavItems: PageSelectorItem[] = [
    { key: 'settings', translationKey: 'organizerPanel.eventSettings', path: '/settings' },
    { key: 'participants', translationKey: 'organizerPanel.eventParticipants', path: '/participants' },
    { key: 'speakers', translationKey: 'organizerPanel.eventSpeakers', path: '/speakers' },
    { key: 'tickets', translationKey: 'organizerPanel.eventTickets', path: '/tickets' },
    { key: 'sponsorships', translationKey: 'organizerPanel.eventSponsorships', path: '/sponsorships' },
    { key: 'mailing', translationKey: 'organizerPanel.eventParticipantMailing', path: '/mailing' },
  ];

  const navItems = isEventMode ? eventNavItems : channelNavItems;

  // Determine active nav key
  const activeNav = useMemo(() => {
    if (isEventMode) {
      if (location.pathname.includes('/settings')) return 'settings';
      if (location.pathname.includes('/participants')) return 'participants';
      if (location.pathname.includes('/speakers')) return 'speakers';
      if (location.pathname.includes('/tickets')) return 'tickets';
      if (location.pathname.includes('/sponsorships')) return 'sponsorships';
      if (location.pathname.includes('/mailing')) return 'mailing';
      return 'settings'; // Default for event mode
    } else {
      if (location.pathname.includes('/settings')) return 'settings';
      if (location.pathname.includes('/members')) return 'members';
      if (location.pathname.includes('/mailing')) return 'mailing';
      if (location.pathname.includes('/revenue')) return 'revenue';
      if (location.pathname.includes('/segments')) return 'segments';
      return 'events';
    }
  }, [isEventMode, location.pathname]);

  // Handle navigation
  const handleNavigate = (path: string) => {
    // Check if we're navigating away from settings page
    const isLeavingSettings = location.pathname.includes('/settings') && !path.includes('/settings');
    
    if (isLeavingSettings) {
      // Check for unsaved changes before navigating
      const navigationBlocked = onNavigateAway(hasUnsavedChanges, () => {
        navigate(path);
      });
      
      // If navigation was blocked, don't proceed
      if (navigationBlocked) {
        return;
      }
    }
    
    // Navigation allowed, proceed normally
    navigate(path);
  };

  // Handle channel selection
  const handleChannelSelect = (channel: ChannelListItem) => {
    const channelTitle = channel.channelTitle || channel.title;
    if (!channelTitle) return;
    const slug = createSlug(channelTitle);
    setIsDropdownOpen(false);
    navigate(`/communities/${slug}/admin`);
  };

  // Handle event selection
  const handleEventSelect = (event: EventListItem) => {
    if (!currentChannel) return;
    const channelTitle = currentChannel.channelTitle || currentChannel.title;
    if (!channelTitle) return;
    const channelSlug = createSlug(channelTitle);
    setIsDropdownOpen(false);
    const eventTitle = event.eventTitle || event.title;
    navigate(generateEventAdminUrl(eventTitle, event.eventID, channelSlug));
  };

  if (channelsLoading || (isEventMode && eventsLoading)) {
    return (
      <>
        <SEO title={getTranslation('organizerPanel.title') || 'Organizer Panel'} noindex />
        <div className={styles.organizerPanelPage}>
          <LoadingSkeleton />
        </div>
      </>
    );
  }

  // Show unauthorized page if user is not admin of existing channel
  if (shouldShowUnauthorized) {
    return (
      <>
        <SEO title={getTranslation('unauthorized.title') || 'Unauthorized'} noindex />
        <UnauthorizedPage />
      </>
    );
  }

  // If channel doesn't exist, return null (will be handled by 404 route)
  if (!currentChannel) {
    return null;
  }

  const channelTitle = currentChannel.channelTitle || currentChannel.title;
  const channelLogo = currentChannel.channelLogo || currentChannel.logo;
  
  // In event mode, show loading if events are still loading, but don't block rendering
  // The pages will handle their own empty states
  const basePath = isEventMode
    ? (() => {
        const eventPath = extractEventPathFromAdminUrl(location.pathname);
        return eventPath ? `/communities/${orgName}/admin/${eventPath}` : `/communities/${orgName}/admin`;
      })()
    : `/communities/${orgName}/admin`;

  const activeNavLabel = navItems.find((i) => i.key === activeNav)?.translationKey
    ? getTranslation(navItems.find((i) => i.key === activeNav)!.translationKey)
    : getTranslation('organizerPanel.title');

  const panelEntityTitle = isEventMode
    ? (effectiveCurrentEvent ? (effectiveCurrentEvent.eventTitle || effectiveCurrentEvent.title) : getTranslation('organizerPanel.eventDetails'))
    : channelTitle;

  const seoTitle = `${activeNavLabel || getTranslation('organizerPanel.title')} - ${panelEntityTitle || ''}`.trim();

  // Render functions for SelectorDropdown
  const renderChannelCurrentItem = (channel: ChannelListItem) => {
    const chTitle = channel.channelTitle || channel.title;
    const chLogo = channel.channelLogo || channel.logo;
    return (
      <>
        {chLogo ? (
          <img
            src={chLogo}
            alt={chTitle}
            className={styles.channelSelectorLogo}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <ChannelInitialIcon 
            channelName={chTitle}
            className={styles.channelSelectorLogo}
            size={32}
          />
        )}
        <span className={styles.channelSelectorName}>{chTitle}</span>
      </>
    );
  };

  const renderChannelItem = (channel: ChannelListItem, isActive: boolean) => {
    const chTitle = channel.channelTitle || channel.title;
    const chLogo = channel.channelLogo || channel.logo;
    return (
      <button
        className={`${styles.channelOption} ${isActive ? styles.channelOptionActive : ''}`}
        onClick={() => handleChannelSelect(channel)}
      >
        <div className={styles.channelOptionContent}>
          {chLogo ? (
            <img
              src={chLogo}
              alt={chTitle}
              className={styles.channelOptionLogo}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <ChannelInitialIcon 
              channelName={chTitle}
              className={styles.channelOptionLogo}
              size={24}
            />
          )}
          <span className={styles.channelOptionName}>{chTitle}</span>
        </div>
      </button>
    );
  };

  const renderEventCurrentItem = (event: EventListItem) => {
    const eventTitle = event.eventTitle || event.title;
    const eventCover = event.cover || event.image || event.bannerImage;
    return (
      <>
        {eventCover ? (
          <img
            src={eventCover}
            alt={eventTitle}
            className={styles.eventSelectorLogo}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <ChannelInitialIcon 
            channelName={eventTitle}
            className={styles.channelSelectorLogo}
            size={32}
          />
        )}
        <span className={styles.channelSelectorName}>{eventTitle}</span>
      </>
    );
  };

  const renderEventItem = (event: EventListItem, isActive: boolean) => {
    const eventTitle = event.eventTitle || event.title;
    const eventCover = event.cover || event.image || event.bannerImage;
    return (
      <button
        className={`${styles.channelOption} ${isActive ? styles.channelOptionActive : ''}`}
        onClick={() => handleEventSelect(event)}
      >
        <div className={styles.channelOptionContent}>
          {eventCover ? (
            <img
              src={eventCover}
              alt={eventTitle}
              className={styles.eventOptionLogo}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <ChannelInitialIcon 
              channelName={eventTitle}
              className={styles.channelOptionLogo}
              size={24}
            />
          )}
          <span className={styles.channelOptionName}>{eventTitle}</span>
        </div>
      </button>
    );
  };

  return (
    <>
      <SEO
        title={seoTitle}
        url={typeof window !== 'undefined' ? `${window.location.origin}${location.pathname}` : undefined}
        noindex
        lang={language.language}
      />
      <div className={styles.organizerPanelPage}>
        <div className={styles.contentWrapper}>
        <div className={styles.sidebar}>
          {isEventMode ? (
            effectiveCurrentEvent ? (
              <SelectorDropdown
                items={sortedEvents}
                currentItem={effectiveCurrentEvent}
                onSelect={handleEventSelect}
                isOpen={isDropdownOpen}
                onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
                renderItem={renderEventItem}
                renderCurrentItem={renderEventCurrentItem}
                maxVisibleItems={3}
                getItemId={(event) => event.eventID}
                isItemActive={(event, current) => current ? event.eventID === current.eventID : false}
              />
            ) : (
              <div className={styles.channelSelector}>
                <div className={styles.channelSelectorButton} style={{ cursor: 'default' }}>
                  <div className={styles.channelSelectorContent}>
                    <span className={styles.channelSelectorName}>Loading event...</span>
                  </div>
                </div>
              </div>
            )
          ) : (
            <SelectorDropdown
              items={adminChannels}
              currentItem={currentChannel}
              onSelect={handleChannelSelect}
              isOpen={isDropdownOpen}
              onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
              renderItem={renderChannelItem}
              renderCurrentItem={renderChannelCurrentItem}
              maxVisibleItems={3}
              getItemId={(channel) => channel.channelID}
              isItemActive={(channel, current) => current ? channel.channelID === current.channelID : false}
            />
          )}
          <PageSelector
            items={navItems}
            activeKey={activeNav}
            onNavigate={handleNavigate}
            basePath={basePath}
          />
          {isEventMode && (
            <Button
              variant="secondary"
              size="medium"
              onClick={() => navigate(`/communities/${orgName}/admin`)}
              className={styles.backToChannelButton}
            >
              {getTranslation('organizerPanel.backToChannel') || 'Topluluğa Dön'}
            </Button>
          )}
        </div>
        <div className={styles.mainContent}>
          <div key={activeNav} className={styles.outletTransition}>
            <Outlet context={{
              channel: currentChannel,
              channelTitle,
              channelLogo,
              event: effectiveCurrentEvent,
              eventTitle: effectiveCurrentEvent ? (effectiveCurrentEvent.eventTitle || effectiveCurrentEvent.title) : null,
              eventLogo: effectiveCurrentEvent ? (effectiveCurrentEvent.cover || effectiveCurrentEvent.image || effectiveCurrentEvent.bannerImage) : null,
              refreshChannel: refreshChannels
            }} />
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default OrganizerPanelPage;

